import type { FetchHttpBearer } from "@dtelecom/secure-chat-client"
import { getAccessToken } from "@privy-io/react-auth"

import type {
  CurrentUser,
  MeetingHistoryHost,
  RoomHistoryItem,
  User,
  WorkspaceSummary,
} from "./types"

interface WithAvatar {
  avatar: string
}

interface WithImage {
  image: string
}

const UNIX_MILLISECONDS_THRESHOLD = 1_000_000_000_000

export const toUnixMilliseconds = (
  value?: number | string | null,
): number | undefined => {
  if (value == null || value === "") {
    return undefined
  }

  const numericValue = typeof value === "number" ? value : Number(value)

  if (Number.isFinite(numericValue)) {
    return numericValue >= UNIX_MILLISECONDS_THRESHOLD
      ? numericValue
      : numericValue * 1000
  }

  if (typeof value !== "string") {
    return undefined
  }

  const parsedDate = Date.parse(value)
  return Number.isNaN(parsedDate) ? undefined : parsedDate
}

export function formatUser<T extends WithAvatar>(user: T): T {
  const avatar = formatAvatarUrl(user.avatar)
  return { ...user, avatar } as T
}

export function formatMeeting<T extends WithImage>(meeting: T): T {
  const image = formatAvatarUrl(meeting.image)
  return { ...meeting, image } as T
}

export function formatMeetingHistoryHost(
  host: MeetingHistoryHost,
): MeetingHistoryHost {
  return {
    ...host,
    avatar: formatAvatarUrl(host.avatar),
  }
}

export function formatRoomHistoryItem(item: RoomHistoryItem): RoomHistoryItem {
  return {
    ...formatMeeting(item),
    host: item.host ? formatMeetingHistoryHost(item.host) : undefined,
  }
}

export function formatUsers<T extends User>(users: T[]): T[] {
  return users.map(formatUser)
}

export function formatMeetings<T extends WithImage>(meetings: T[]): T[] {
  return meetings.map(formatMeeting)
}

export function formatRoomHistoryItems(
  items: RoomHistoryItem[],
): RoomHistoryItem[] {
  return items.map(formatRoomHistoryItem)
}

export function formatWorkspaceSummary(
  summary: WorkspaceSummary,
): WorkspaceSummary {
  return {
    ...summary,
    avatar: formatAvatarUrl(summary.avatar),
  }
}

export function formatCurrentUser(user: CurrentUser): CurrentUser {
  const formatted = formatUser(user)
  if (!formatted.workspaces?.length) return formatted
  return {
    ...formatted,
    workspaces: formatted.workspaces.map(formatWorkspaceSummary),
  }
}

export const formatAvatarUrl = (avatar?: string): string => {
  if (!avatar) return ""
  if (avatar.startsWith("http")) return avatar
  return `${import.meta.env.VITE_API_URL}${import.meta.env.VITE_IMAGES_PATH}/${avatar}`
}

export function formatCallHistoryItems<T extends { collocutor: User }>(
  items: T[],
): T[] {
  return items.map((item) => ({
    ...item,
    collocutor: formatUser(item.collocutor),
  }))
}

export function formatCallResponse<
  T extends { call: { host: User; collocutor: User } },
>(data: T): T {
  return {
    ...data,
    call: {
      ...data.call,
      host: formatUser(data.call.host),
      collocutor: formatUser(data.call.collocutor),
    },
  }
}

export const getPrivyAccessToken: FetchHttpBearer = async () => {
  const token = await getAccessToken()
  if (!token) throw new Error("no privy access token")
  return token
}
